/**
 * Safe Markdown-to-editorial adapter.
 *
 * Older Athena replies arrive as Markdown instead of the v1 response
 * presentation envelope. This module translates only a small, predictable
 * Markdown subset into that envelope so the response-design renderer can give
 * well-organised replies an editorial treatment. It never renders HTML, never
 * evaluates Markdown, and deliberately returns `null` for short, casual
 * messages so they keep the normal conversation experience.
 */

import { normalizeResponsePresentation } from './responsePresentation';

const MAX_SOURCE_LENGTH = 32000;
const MAX_PARAGRAPH_LENGTH = 1500;

const HEADING_RE = /^\s{0,3}(#{1,3})\s+(.+?)\s*#*\s*$/;
const DIVIDER_RE = /^\s{0,3}(?:(?:[-*_])\s*){3,}$/;
const CHECKLIST_RE = /^\s*[-*+]\s+\[([ xX])\]\s+(.+)\s*$/;
const BULLET_RE = /^\s*[-*+]\s+(.+)\s*$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.+)\s*$/;
const QUOTE_RE = /^\s{0,3}>\s?(.*)$/;
const FENCE_RE = /^\s*```/;
const BOLD_LABEL_RE = /^\s*(\*\*|__)([^\n]+?)\1\s*$/;

// The editorial reference uses colour to guide the eye through a list, not
// to decorate every word. Keep this deliberately small and repeatable so a
// long answer still feels calm.
const LIST_EMPHASIS_COLORS = Object.freeze(['blue', 'yellow', 'rose', 'blue', 'yellow']);
const HIGHLIGHT_SEGMENT_BUDGET = 2;
const LIST_COLORED_SEGMENT_BUDGET = 5;

function safeText(value) {
  return String(value || '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/<!--(?:.|\n|\r)*?-->/g, '')
    // Raw HTML is not part of the supported Markdown subset. React would
    // escape it anyway, but removing tags keeps the presentation contract
    // strictly text-only.
    .replace(/<\/?[A-Za-z][^>]*>/g, '')
    .trim();
}

function sourceText(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .slice(0, MAX_SOURCE_LENGTH);
}

function safeHref(value) {
  try {
    const href = new URL(String(value || '').trim());
    return href.protocol === 'https:' || href.protocol === 'http:' ? href.href : '';
  } catch {
    return '';
  }
}

function appendText(segments, text) {
  if (!text) return;
  const previous = segments[segments.length - 1];
  if (previous?.type === 'text') {
    previous.text += text;
  } else {
    segments.push({ type: 'text', text });
  }
}

/**
 * Produces only the inline nodes accepted by `normalizeResponsePresentation`:
 * text, bold and http(s) links. Unsupported Markdown stays readable text.
 */
function parseInline(value) {
  const source = safeText(value);
  if (!source) return [];

  const segments = [];
  // Links are intentionally limited to an explicit http(s) URL. The simple
  // grammar keeps this parser predictable and avoids an HTML/Markdown parser.
  const token = /\[([^\]\n]+)\]\(\s*(https?:\/\/[^\s)]+)(?:\s+(?:"[^"]*"|'[^']*'))?\s*\)|(\*\*|__)([\s\S]+?)\3/g;
  let cursor = 0;
  let match;

  while ((match = token.exec(source))) {
    appendText(segments, source.slice(cursor, match.index));
    if (match[1] !== undefined) {
      const label = safeText(match[1]);
      const href = safeHref(match[2]);
      if (label && href) segments.push({ type: 'link', text: label, href });
      else appendText(segments, label || match[0]);
    } else {
      const text = safeText(match[4]);
      if (text) segments.push({ type: 'bold', text });
      else appendText(segments, match[0]);
    }
    cursor = token.lastIndex;
  }
  appendText(segments, source.slice(cursor));

  return segments.filter(segment => segment.text?.trim());
}

function inlineText(value) {
  return parseInline(value).map(segment => segment.text).join('').trim();
}

function paragraphBlocks(value) {
  const source = safeText(value);
  if (!source) return [];

  // The presentation contract caps a block's text length. Splitting a very
  // long paragraph retains the reply instead of silently clipping it.
  const chunks = [];
  let remaining = source;
  while (remaining.length > MAX_PARAGRAPH_LENGTH) {
    const boundary = Math.max(
      remaining.lastIndexOf('. ', MAX_PARAGRAPH_LENGTH),
      remaining.lastIndexOf('! ', MAX_PARAGRAPH_LENGTH),
      remaining.lastIndexOf('? ', MAX_PARAGRAPH_LENGTH),
      remaining.lastIndexOf('; ', MAX_PARAGRAPH_LENGTH),
      remaining.lastIndexOf(' ', MAX_PARAGRAPH_LENGTH),
    );
    const cut = boundary > MAX_PARAGRAPH_LENGTH * 0.55 ? boundary + 1 : MAX_PARAGRAPH_LENGTH;
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  if (remaining) chunks.push(remaining);

  return chunks
    .map(text => ({ type: 'paragraph', content: parseInline(text) }))
    .filter(block => block.content.length);
}

function listKind(line) {
  if (CHECKLIST_RE.test(line)) return 'check';
  if (BULLET_RE.test(line)) return 'bullet';
  if (ORDERED_RE.test(line)) return 'ordered';
  return null;
}

function listMatch(line, kind) {
  if (kind === 'check') {
    const match = line.match(CHECKLIST_RE);
    return match ? { checked: match[1].toLowerCase() === 'x', text: match[2] } : null;
  }
  const match = line.match(kind === 'ordered' ? ORDERED_RE : BULLET_RE);
  return match ? { checked: false, text: match[1] } : null;
}

// A standalone bold fragment is commonly how older replies mark sections
// (for example, `**Pontos fortes:**`). Be deliberately strict here: a long
// bold sentence should remain a normal paragraph, not become a fake heading.
function standaloneBoldLabel(line) {
  const match = line.match(BOLD_LABEL_RE);
  if (!match) return '';

  const label = safeText(match[2]);
  const withoutColon = label.replace(/:\s*$/, '').trim();
  const hasFinalColon = withoutColon.length !== label.length;
  const words = withoutColon ? withoutColon.split(/\s+/) : [];
  const maxWords = hasFinalColon ? 12 : 6;

  if (!withoutColon || label.length > 96 || words.length > maxWords) return '';
  if (/[.!?;]/.test(label) || /[\[\]<>`]/.test(label)) return '';
  return label;
}

function startsBlock(line) {
  return !line.trim()
    || HEADING_RE.test(line)
    || Boolean(standaloneBoldLabel(line))
    || DIVIDER_RE.test(line)
    || QUOTE_RE.test(line)
    || FENCE_RE.test(line)
    || Boolean(listKind(line));
}

function parseMarkdown(content) {
  const source = sourceText(content);
  if (!source) return null;

  const lines = source.split('\n');
  const message = [];
  const sections = [];
  let currentSection = null;
  let title = '';
  const stats = {
    headingCount: 0,
    listItems: 0,
    paragraphCount: 0,
    quoteCount: 0,
    blockCount: 0,
  };

  const addBlock = (block) => {
    if (!block) return;
    const target = currentSection ? currentSection.blocks : message;
    target.push(block);
    stats.blockCount += 1;
    if (block.type === 'paragraph') stats.paragraphCount += 1;
    if (block.type === 'quote') stats.quoteCount += 1;
    if (block.type === 'list') stats.listItems += block.items.length;
  };

  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      const level = heading[1].length;
      const text = inlineText(heading[2]);
      const contentNodes = parseInline(heading[2]);
      stats.headingCount += 1;

      if (level === 1 && !title && !message.length && !sections.length) {
        title = text;
      } else if (level === 2 || (level === 3 && !currentSection)) {
        currentSection = {
          id: `markdown-section-${sections.length + 1}`,
          title: text,
          blocks: [],
        };
        sections.push(currentSection);
      } else if (contentNodes.length) {
        addBlock({ type: 'heading', level: 3, content: contentNodes });
      }
      index += 1;
      continue;
    }

    const boldLabel = standaloneBoldLabel(line);
    if (boldLabel) {
      stats.headingCount += 1;

      // When a Markdown reply opens with an H2 followed by bold labels, that
      // H2 is usually the document title ("Análise do sistema"), while the
      // labels are the real editorial sections. Promote it so we avoid an
      // empty section before "Pontos fortes".
      if (!title && !message.length && sections.length === 1 && currentSection?.blocks.length === 0) {
        title = currentSection.title;
        sections.pop();
      }

      currentSection = {
        id: `markdown-section-${sections.length + 1}`,
        title: boldLabel,
        blocks: [],
      };
      sections.push(currentSection);
      index += 1;
      continue;
    }

    if (DIVIDER_RE.test(line)) {
      addBlock({ type: 'divider' });
      index += 1;
      continue;
    }

    if (FENCE_RE.test(line)) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !FENCE_RE.test(lines[index])) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      paragraphBlocks(codeLines.join('\n')).forEach(addBlock);
      continue;
    }

    if (QUOTE_RE.test(line)) {
      const quoteLines = [];
      while (index < lines.length) {
        const quote = lines[index].match(QUOTE_RE);
        if (!quote) break;
        quoteLines.push(quote[1]);
        index += 1;
      }
      const quoteContent = parseInline(quoteLines.join(' ').trim());
      if (quoteContent.length) addBlock({ type: 'quote', content: quoteContent });
      continue;
    }

    const kind = listKind(line);
    if (kind) {
      const items = [];
      while (index < lines.length) {
        const item = listMatch(lines[index], kind);
        if (!item) break;
        index += 1;

        const continuation = [];
        while (index < lines.length && /^\s{2,}\S/.test(lines[index]) && !listKind(lines[index])) {
          continuation.push(lines[index].trim());
          index += 1;
        }
        const itemContent = parseInline([item.text, ...continuation].join(' '));
        if (itemContent.length) items.push({ content: itemContent, ...(kind === 'check' ? { checked: item.checked } : {}) });

        if (!lines[index]?.trim()) break;
      }
      if (items.length) addBlock({ type: 'list', style: kind, items });
      continue;
    }

    const paragraph = [];
    while (index < lines.length && !startsBlock(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    paragraphBlocks(paragraph.join(' ')).forEach(addBlock);
  }

  const meaningfulLength = safeText(source)
    .replace(/[`*_#[\]()>-]/g, '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .length;

  return { title, message, sections, stats: { ...stats, meaningfulLength } };
}

function isEditorialStructure(stats) {
  const { headingCount, listItems, paragraphCount, quoteCount, blockCount, meaningfulLength } = stats;
  if (meaningfulLength < 60) return false;

  const headedReply = headingCount >= 1
    && meaningfulLength >= 70
    && (paragraphCount >= 1 || listItems >= 1 || quoteCount >= 1);
  const usefulList = listItems >= 3 && meaningfulLength >= 90;
  const longOrganisedReply = blockCount >= 4 && paragraphCount >= 2 && meaningfulLength >= 220;
  const mixedReply = meaningfulLength >= 130
    && headingCount + (listItems ? 1 : 0) + quoteCount >= 2;

  return headedReply || usefulList || longOrganisedReply || mixedReply;
}

// A single warm highlight gives the opening sentence a useful visual anchor.
// It is intentionally limited to the first bold fragment in the top-level
// introduction; every other prose emphasis remains normal bold text.
function highlightIntroduction(message, highlightCount = 0) {
  let highlighted = false;
  let nextHighlightCount = highlightCount;

  const blocks = message.map((block) => {
    if (highlighted || nextHighlightCount >= HIGHLIGHT_SEGMENT_BUDGET || block.type !== 'paragraph') return block;

    const boldIndex = block.content.findIndex(segment => segment.type === 'bold');
    if (boldIndex < 0) return block;

    highlighted = true;
    nextHighlightCount += 1;
    return {
      ...block,
      content: block.content.map((segment, index) => (
        index === boldIndex
          ? { type: 'highlight', text: segment.text, color: 'yellow' }
          : segment
      )),
    };
  });

  return { blocks, highlightCount: nextHighlightCount };
}

// Colour only existing Markdown emphasis in list items. Text nodes and links
// are carried through unchanged, which keeps the adapter a presentation layer
// rather than a content rewrite.
function colourListEmphasis(blocks, listItemOffset, coloredCount = 0) {
  let itemOffset = listItemOffset;
  let nextColoredCount = coloredCount;
  const nextBlocks = blocks.map((block) => {
    if (block.type !== 'list') return block;

    let changed = false;
    const items = block.items.map((item) => {
      const color = LIST_EMPHASIS_COLORS[itemOffset % LIST_EMPHASIS_COLORS.length];
      itemOffset += 1;
      let itemChanged = false;
      const content = item.content.map((segment) => {
        if (segment.type !== 'bold' || nextColoredCount >= LIST_COLORED_SEGMENT_BUDGET) return segment;

        changed = true;
        itemChanged = true;
        nextColoredCount += 1;
        return { type: 'colored', text: segment.text, color };
      });
      return itemChanged ? { ...item, content } : item;
    });

    return changed ? { ...block, items } : block;
  });

  return { blocks: nextBlocks, itemOffset, coloredCount: nextColoredCount };
}

function decorateEditorialMarkdown(parsed) {
  const introduction = highlightIntroduction(parsed.message);
  const decoratedMessage = colourListEmphasis(introduction.blocks, 0);
  let itemOffset = decoratedMessage.itemOffset;
  let coloredCount = decoratedMessage.coloredCount;

  const sections = parsed.sections.map((section) => {
    const decoratedBlocks = colourListEmphasis(section.blocks, itemOffset, coloredCount);
    itemOffset = decoratedBlocks.itemOffset;
    coloredCount = decoratedBlocks.coloredCount;
    return decoratedBlocks.blocks === section.blocks
      ? section
      : { ...section, blocks: decoratedBlocks.blocks };
  });

  return {
    ...parsed,
    message: decoratedMessage.blocks,
    sections,
  };
}

/**
 * True only when Markdown has enough visible organisation to benefit from the
 * editorial layout. It deliberately rejects concise chat replies.
 */
export function shouldUseEditorialMarkdown(content) {
  const parsed = parseMarkdown(content);
  return Boolean(parsed && isEditorialStructure(parsed.stats));
}

/**
 * Derives a valid v1 `conversation` presentation from safe Markdown.
 * `null` means the caller should use its legacy/plain conversation renderer.
 */
export function deriveEditorialPresentation(content) {
  const parsed = parseMarkdown(content);
  if (!parsed || !isEditorialStructure(parsed.stats)) return null;

  const editorial = decorateEditorialMarkdown(parsed);

  return normalizeResponsePresentation({
    version: 1,
    mode: 'conversation',
    ...(editorial.title ? { title: editorial.title } : {}),
    ...(editorial.message.length ? { message: editorial.message } : {}),
    ...(editorial.sections.length ? { sections: editorial.sections } : {}),
  });
}
